import VerifiedIcon from '@mui/icons-material/Verified';
import {CircularProgress} from '@mui/material';
import {CustomContentProps, useSnackbar} from 'notistack';
import React, {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {SnackbarContent as CustomSnackbarContent} from './SnackbarContent';
import {useExtensionCommands} from '../../hooks/extension/ExtensionProvider';
import {useExtensionCommandStatusQuery} from '../../hooks/extensionCommand/queries/useExtensionCommandStatusQuery';

interface WorkingCommandProps extends CustomContentProps {
  commandId: string;
}

// `_internalName` des commandes qui ont un libellé `extension.command.<nom>` dans les locales.
// Toute commande absente d'ici s'affiche « Commande inconnue » : quand l'API enfile un nouveau
// type de commande, ajouter son nom ici ET la clé dans les six `translation.json`.
const LABELLED_COMMANDS = [
  'content-creation-share-previews-by-share-life-cycle-state',
  'get-feed-dash-profile-updates-by-member-comments',
  'get-feed-dash-profile-updates-by-member-reactions',
  'get-feed-dash-profile-updates-by-member-share-feed',
  'get-premium-dash-analytics-view',
  'get-premium-dash-analytics-view-by-analytics-entity',
  'profile-cards-by-deferred-cards',
  'profile-cards-by-initial-cards',
  'search-reusable-typeahead-by-blended',
  'share-details-by-share-urn',
  'share-get-draft-media-for-preview',
  'top-card-supplementary-query',
  'voyagerApiIdentityDashClusters',
] as const;
type LabelledCommand = (typeof LABELLED_COMMANDS)[number];

function isLabelledCommand(name: string): name is LabelledCommand {
  return (LABELLED_COMMANDS as readonly string[]).includes(name);
}

const WorkingCommand = React.forwardRef<HTMLDivElement, WorkingCommandProps>(({id, commandId}, ref) => {
  const {closeSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const {currentCommandId, getCommandById, dismissFinishedCommand} = useExtensionCommands();
  const currentCommand = getCommandById(commandId);

  // Le nom interne de la commande, pour son libellé : lu dès le montage, la
  // commande n'a pas besoin d'être finie pour le connaître.
  const {data: commandStatus} = useExtensionCommandStatusQuery(commandId);
  const commandName = commandStatus?.command._internalName;

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  useEffect(() => {
    if (currentCommand?.finished) {
      setTimeout(() => {
        dismissFinishedCommand(commandId);
        handleDismiss();
      }, 3000);
    }
  }, [currentCommand]);

  return (
    <CustomSnackbarContent
      ref={ref}
      color={currentCommand?.finished ? 'success' : 'info'}
      icon={commandId === currentCommandId ? <CircularProgress color="inherit" size={20} /> : <VerifiedIcon />}
      title={t('extension.workingTitle')}
      content={
        commandName !== undefined && isLabelledCommand(commandName)
          ? t(`extension.command.${commandName}`)
          : t('extension.command.unknown')
      }
      onClose={handleDismiss}
      bgWhite
      titleColor={currentCommand?.finished ? 'success' : 'info'}
      dividerColor={false}
    />
  );
});

export {WorkingCommand};
